import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [currentScore, setCurrentScore] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Get user info from token
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Decode token to get organization_id
      const payload = JSON.parse(atob(token.split('.')[1]));
      const orgId = payload.organization_id;

      // Load organization
      const orgResponse = await axios.get(`/organizations/${orgId}`);
      setOrganization(orgResponse.data);

      // Load metrics
      const metricsResponse = await axios.get('/metrics');
      setMetrics(metricsResponse.data);

      // Try to load current score (may not exist yet)
      try {
        const scoreResponse = await axios.get('/scores/current');
        setCurrentScore(scoreResponse.data);
      } catch (scoreError) {
        // No score yet - this is fine for new organizations
        console.log('No trust score calculated yet');
      }

      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto" data-testid="loading-spinner"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm" data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="org-name">
                {organization?.name || 'Trust Index'}
              </h1>
              <p className="text-sm text-gray-600">Brand Trust Measurement Platform</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" data-testid="dashboard-error">
            {error}
          </div>
        )}

        {/* Trust Score Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8" data-testid="trust-score-card">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Current Trust Score</h2>
          
          {currentScore ? (
            <div className="space-y-6">
              <div className="text-center">
                <div className="text-6xl font-bold text-purple-600 mb-2" data-testid="overall-score">
                  {currentScore.score.toFixed(1)}
                </div>
                <p className="text-gray-600">Overall Trust Score (0-100)</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-700" data-testid="authenticity-score">
                    {currentScore.authenticity_score.toFixed(1)}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Authenticity</p>
                </div>

                <div className="bg-indigo-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-indigo-700" data-testid="accountability-score">
                    {currentScore.accountability_score.toFixed(1)}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Accountability</p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700" data-testid="community-score">
                    {currentScore.community_score.toFixed(1)}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Community</p>
                </div>
              </div>

              <p className="text-xs text-gray-500 text-center">
                Last calculated: {new Date(currentScore.calculated_at).toLocaleString()}
              </p>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Trust Score Yet</h3>
              <p className="text-gray-600 mb-4">
                Start by entering metric values to calculate your first trust score.
              </p>
              <p className="text-sm text-gray-500">
                This will be available in Phase 2 - Trust Index Model
              </p>
            </div>
          )}
        </div>

        {/* Metrics Overview */}
        <div className="bg-white rounded-xl shadow-lg p-8" data-testid="metrics-section">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Available Metrics</h2>
          
          {metrics.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {metrics.map((metric) => (
                <div
                  key={metric.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition"
                  data-testid="metric-card"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{metric.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{metric.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      {metric.data_type}
                    </span>
                    <span className="text-gray-500">Weight: {(metric.weight * 100).toFixed(0)}%</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No metrics available
            </div>
          )}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <h3 className="font-semibold mb-2">Phase 1 Complete</h3>
            <p className="text-sm text-purple-100">
              Infrastructure and API layer established with authentication and metrics management.
            </p>
          </div>

          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-6 text-white">
            <h3 className="font-semibold mb-2">Total Metrics</h3>
            <p className="text-3xl font-bold mb-1" data-testid="total-metrics">{metrics.length}</p>
            <p className="text-sm text-indigo-100">Across 7 sub-pillars</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <h3 className="font-semibold mb-2">Coming in Phase 2</h3>
            <p className="text-sm text-blue-100">
              Trust score calculation engine and normalization algorithms.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default DashboardPage;
